/**
 * Crown Jewels Widget
 * 
 * Displays user's earned Crown Jewels (gemstone-themed achievement badges).
 * Each jewel shows what it was earned for and the gemstone's meaning.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Gem, ArrowRight, Loader2, Sparkles, Lock, Check,
  ChevronRight, Info, Crown
} from 'lucide-react';

const API_URL = window.location.origin;

// Gemstone emoji mapping
const JEWEL_ICONS = {
  rose_quartz: '💗',
  moonstone: '🌙',
  citrine: '🌟',
  amethyst: '💜',
  sapphire: '💎',
  diamond: '✨',
  pearl: '🤍',
  opal: '🌈',
  turquoise: '💠',
  aquamarine: '🩵',
  lapis_lazuli: '🔵',
  tigers_eye: '🐯',
  garnet: '❤️',
  ruby: '🔴',
  emerald: '💚',
  peridot: '🍀',
  onyx: '🖤',
  jade: '🪨',
  topaz: '🧡',
  alexandrite: '🪻',
  amber: '🟠',
  coral: '🪸',
  pink_tourmaline: '🩷',
  tanzanite: '💙'
};

const CrownJewelsWidget = ({ className = '', onNewJewels = null }) => {
  const [jewelData, setJewelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [showAllJewels, setShowAllJewels] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('journey');
  const [selectedJewel, setSelectedJewel] = useState(null);
  const [newJewelsCount, setNewJewelsCount] = useState(0);

  useEffect(() => {
    fetchJewels();
    // Auto-check for new jewels on mount
    checkForNewJewels(true);
  }, []);

  const fetchJewels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/crown-jewels/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJewelData(response.data);
    } catch (error) {
      console.error('Failed to fetch crown jewels:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewJewels = async (silent = false) => {
    if (!silent) setChecking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/crown-jewels/check`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.awarded?.length > 0) {
        setNewJewelsCount(response.data.awarded.length);
        if (!silent) {
          response.data.awarded.forEach(item => {
            toast.success(item.message, { duration: 5000 });
          });
        }
        // Notify parent component about new jewels
        if (onNewJewels) {
          onNewJewels(response.data.awarded.length);
        }
        fetchJewels();
      } else if (!silent) {
        toast.info('No new jewels discovered yet. Keep shining!');
      }
    } catch (error) {
      if (!silent) toast.error('Failed to check for new jewels');
    } finally {
      if (!silent) setChecking(false);
    }
  };

  const handleCheckJewels = async () => {
    setNewJewelsCount(0);  // Clear the badge when manually checking
    await checkForNewJewels(false);
  };

  if (loading) {
    return (
      <Card className={`card-base ${className}`} data-testid="crown-jewels-widget">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
        </CardContent>
      </Card>
    );
  }

  if (!jewelData) {
    return null;
  }

  const { jewels_by_category, stats, categories } = jewelData;
  
  // Get recently earned jewels (last 3)
  const allEarnedJewels = Object.values(jewels_by_category)
    .flat()
    .filter(j => j.earned)
    .sort((a, b) => new Date(b.earned_at) - new Date(a.earned_at));
  
  const recentJewels = allEarnedJewels.slice(0, 3);
  
  // Get next available jewels to earn
  const nextToEarn = Object.values(jewels_by_category)
    .flat()
    .filter(j => !j.earned)
    .slice(0, 2);

  return (
    <>
      <Card className={`card-base ${className}`} data-testid="crown-jewels-widget">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" />
              Your Crown Jewels
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllJewels(true)}
              className="text-terracotta hover:text-terracotta/80"
            >
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">
                {stats.earned_jewels} of {stats.total_jewels} jewels collected
              </span>
              <span className="text-sm font-medium text-terracotta">
                {Math.round(stats.completion_percent)}%
              </span>
            </div>
            <Progress value={stats.completion_percent} className="h-2" />
            <p className="text-xs text-gray-500 mt-1">
              +{stats.total_points_from_jewels} diamonds from jewels
            </p>
          </div>

          {/* Recent Jewels */}
          {recentJewels.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-600">Recently Earned</p>
              <div className="flex gap-2 flex-wrap">
                {recentJewels.map(jewel => (
                  <button
                    key={jewel.id}
                    onClick={() => setSelectedJewel(jewel)}
                    className="flex items-center gap-2 p-2 rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 hover:shadow-md transition-all cursor-pointer"
                    title={`Click to learn more about ${jewel.name}`}
                    data-testid={`jewel-${jewel.id}`}
                  >
                    <span 
                      className="text-xl"
                      style={{ textShadow: '0 0 8px ' + jewel.color }}
                    >
                      {JEWEL_ICONS[jewel.id] || '💎'}
                    </span>
                    <div className="text-left">
                      <p className="text-sm font-medium text-forest">{jewel.name}</p>
                      <p className="text-xs text-gray-500">+{jewel.points} diamonds</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <Gem className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No jewels collected yet</p>
              <p className="text-gray-400 text-xs">Complete activities to earn Crown Jewels!</p>
            </div>
          )}

          {/* Next to Earn */}
          {nextToEarn.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-600 mb-2">Up Next</p>
              <div className="space-y-2">
                {nextToEarn.map(jewel => (
                  <button
                    key={jewel.id}
                    onClick={() => setSelectedJewel(jewel)}
                    className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 opacity-75 w-full text-left hover:bg-gray-100 transition-all"
                  >
                    <div className="text-xl grayscale">{JEWEL_ICONS[jewel.id] || '💎'}</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">{jewel.name}</p>
                      <p className="text-xs text-gray-400">{jewel.how_to_earn || jewel.criteria}</p>
                    </div>
                    <Lock className="h-4 w-4 text-gray-300" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Check for New Jewels */}
          <Button
            variant="outline"
            onClick={handleCheckJewels}
            disabled={checking}
            className="w-full mt-4 relative"
          >
            {checking ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Discovering...</>
            ) : newJewelsCount > 0 ? (
              <><Sparkles className="h-4 w-4 mr-2" />New Jewels Available!</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" />Check for New Jewels</>
            )}
            {newJewelsCount > 0 && (
              <span className="absolute -top-2 -right-2 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                {newJewelsCount}
              </span>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* All Jewels Dialog */}
      <Dialog open={showAllJewels} onOpenChange={setShowAllJewels}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" />
              Crown Jewels Collection ({stats.earned_jewels}/{stats.total_jewels})
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
              <TabsList className="grid w-full grid-cols-4 mb-4">
                {categories.map(cat => (
                  <TabsTrigger key={cat.id} value={cat.id} className="text-xs">
                    {cat.label.split(' ')[0]}
                  </TabsTrigger>
                ))}
              </TabsList>

              {categories.map(cat => (
                <TabsContent key={cat.id} value={cat.id} className="space-y-3">
                  <p className="text-sm text-gray-500 mb-2">{cat.description}</p>
                  {jewels_by_category[cat.id]?.map(jewel => (
                    <button
                      key={jewel.id}
                      onClick={() => setSelectedJewel(jewel)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                        jewel.earned
                          ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200 hover:shadow-md'
                          : 'bg-gray-50 border-gray-200 opacity-60 hover:opacity-80'
                      }`}
                    >
                      <div 
                        className={`text-2xl ${jewel.earned ? '' : 'grayscale'}`}
                        style={{ 
                          filter: jewel.earned ? 'none' : 'grayscale(1)',
                          textShadow: jewel.earned ? '0 0 8px ' + jewel.color : 'none'
                        }}
                      >
                        {JEWEL_ICONS[jewel.id] || '💎'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${jewel.earned ? 'text-forest' : 'text-gray-500'}`}>
                            {jewel.name}
                          </p>
                          {jewel.earned && (
                            <Check className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{jewel.description}</p>
                        <p className="text-xs text-gray-400 mt-1">{jewel.how_to_earn || jewel.criteria}</p>
                      </div>
                      <div className="text-right">
                        <Badge 
                          variant={jewel.earned ? "default" : "outline"}
                          className={jewel.earned ? "bg-amber-500" : ""}
                        >
                          +{jewel.points}
                        </Badge>
                        {jewel.earned && jewel.earned_at && (
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(jewel.earned_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      {/* Jewel Detail Dialog */}
      <Dialog open={!!selectedJewel} onOpenChange={() => setSelectedJewel(null)}>
        <DialogContent className="sm:max-w-md">
          {selectedJewel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <span 
                    className="text-2xl"
                    style={{ textShadow: selectedJewel.earned ? '0 0 12px ' + selectedJewel.color : 'none' }}
                  >
                    {JEWEL_ICONS[selectedJewel.id] || '💎'}
                  </span>
                  {selectedJewel.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                {/* Status Badge */}
                <div className="flex items-center justify-between">
                  <Badge 
                    variant={selectedJewel.earned ? "default" : "outline"}
                    className={`${selectedJewel.earned ? "bg-green-500" : ""} text-sm`}
                  >
                    {selectedJewel.earned ? '✓ Earned' : 'Locked'}
                  </Badge>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    +{selectedJewel.points} diamonds
                  </Badge>
                </div>

                {/* Description */}
                <div className="bg-gradient-to-br from-gray-50 to-amber-50/30 rounded-lg p-4">
                  <p className="text-gray-700 font-medium mb-2">{selectedJewel.description}</p>
                  
                  {/* How to Earn */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">How to earn:</p>
                    <p className="text-sm text-gray-600">{selectedJewel.how_to_earn || selectedJewel.criteria}</p>
                  </div>
                </div>

                {/* Gemstone Meaning */}
                {selectedJewel.gem_meaning && (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <p className="text-xs font-semibold text-purple-500 uppercase mb-1 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Crystal Energy
                    </p>
                    <p className="text-sm text-purple-700 italic">{selectedJewel.gem_meaning}</p>
                  </div>
                )}

                {/* Earned Date */}
                {selectedJewel.earned && selectedJewel.earned_at && (
                  <div className="text-center text-sm text-gray-500">
                    <span className="font-medium">Earned on:</span>{' '}
                    {new Date(selectedJewel.earned_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CrownJewelsWidget;
